import { createTheme } from "@mui/material/styles"
import { createBreakpoints } from "@mui/system"
import { downHeight, upHeight } from "themes/helpers/breakpoint"
import { variantMuiGrid } from "./variant/muiGrid"
import { getPalette, palette } from "./palette"
import { typography } from "./typography"
import { components } from "./components"
import {
  container,
  toolbar,
  footer,
  activityCircle,
  activityPath,
  layout,
  checkIcon,
  wrongIcon,
  matchingContainer,
  matchingBoxs,
  matchingBox2,
  matchingBox3,
  matchingBox4,
  matchingBox5,
  matchingBox6,
  matchingBox8,
  textMax3Lines,
  key,
  keys,
  grammarDetails,
  tabs,
  tab,
  teacherNotes,
  modalContent,
  mainLayout,
  switchButton,
} from "./mixins"

const themeBreakpoints = createBreakpoints({
  values: {
    lg: 1200,
    lgH: 1000,
    md: 900,
    mdH: 800,
    sm: 600,
    xsH: 500,
    smH: 700,
    xl: 1536,
    xs: 0,
    potrait: 820,
  },
})

themeBreakpoints.downHeight = downHeight
themeBreakpoints.upHeight = upHeight

export const getTheme = ({ fontFamily, fontSize, bgColor, mode = "light" }) =>
  createTheme({
    cssVariables: true,
    palette: getPalette(mode),
    breakpoints: themeBreakpoints,
    typography: typography({ fontFamily, fontSize, mode }),
    colors: {
      brand: {
        primary: "var(--primary-color)",
        variants: {
          lighter1: "var(--primary-color-lighter-1)",
          lighter2: "var(--primary-color-lighter-2)",
        },
      },
      font: {
        primary: "var(--primary-font-color)",
        variants: {
          lighter1: "var(--primary-font-color-lighter-1)",
        },
      },
      hover: {
        primary: "var(--primary-hover-color)",
      },
      border: {
        primary: "var(--primary-border-color)",
      },
      views: {
        background: "var(--primary-view-color)",
      },
    },
    zIndex: {
      z1: 10,
      z2: 20,
      z3: 30,
      z4: 40,
      z5: 50,
      z6: 60,
      z7: 70,
      z8: 80,
      z9: 90,
      z10: 100,
    },
    components: {
      ...variantMuiGrid,
      ...components,
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundColor: mode === "light" ? bgColor : "#1d2329",
            ...(mode === "dark" && {
              backgroundImage: "none",
            }),
          },
        },
      },
      MuiDialogContent: {
        styleOverrides: {
          root: {
            backgroundColor: mode === "light" ? bgColor : "#1d2329",
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: mode === "light" ? bgColor : "#1d2329",
            color: mode === "light" ? "#000" : "#b6bec9",
            boxShadow: "0px 0px 10px rgba(0,0,0,0.1)",
          },
          arrow: {
            color: mode === "light" ? bgColor : "#1d2329",
          },
        },
      },
    },
    mixins: {
      container,
      toolbar,
      footer,
      activityCircle,
      activityPath,
      checkIcon,
      wrongIcon,
      layout,
      matchingContainer,
      matchingBoxs,
      matchingBox2,
      matchingBox3,
      matchingBox4,
      matchingBox5,
      matchingBox6,
      matchingBox8,
      textMax3Lines,
      key,
      keys,
      grammarDetails,
      tabs,
      tab,
      teacherNotes,
      modalContent,
    },
    shadows: [
      "none",
      "0px 3px 6px rgba(0,0,0,2%)",
      "0px 6px 6px -3px rgba(0,0,0,0.2),0px 10px 14px 1px rgba(0,0,0,0.14),0px 4px 18px 3px rgba(0,0,0,0.12)",
      "0px 3px 3px -2px rgba(0,0,0,0.2),0px 3px 4px 0px rgba(0,0,0,0.14),0px 1px 8px 0px rgba(0,0,0,0.12)",
      "0px 2px 4px -1px rgba(0,0,0,0.2),0px 4px 5px 0px rgba(0,0,0,0.14),0px 1px 10px 0px rgba(0,0,0,0.12)",
      "0 3px 10px rgb(34 34 34 / 20%)",
      "0px 0px 5px 0px rgb(153 153 153)",
      "rgb(0 0 0 / 12%) 0px 1px 6px, rgb(0 0 0 / 12%) 0px 1px 4px",
      "3px 3px 10px -1px rgba(0,0,0,0.15)",
      "0px 0px 15px -6px rgb(0 0 0 / 75%)",
      "3px 3px 10px -1px rgb(0 0 0 / 45%)",
      // defaults from 11 to 24
      "0px 6px 7px -4px rgba(0,0,0,0.2),0px 11px 15px 1px rgba(0,0,0,0.14),0px 4px 20px 3px rgba(0,0,0,0.12)",
      "0px 7px 8px -4px rgba(0,0,0,0.2),0px 12px 17px 2px rgba(0,0,0,0.14),0px 5px 22px 4px rgba(0,0,0,0.12)",
      "0px 7px 8px -4px rgba(0,0,0,0.2),0px 13px 19px 2px rgba(0,0,0,0.14),0px 5px 24px 4px rgba(0,0,0,0.12)",
      "0px 7px 9px -4px rgba(0,0,0,0.2),0px 14px 21px 2px rgba(0,0,0,0.14),0px 5px 26px 4px rgba(0,0,0,0.12)",
      "0px 8px 9px -5px rgba(0,0,0,0.2),0px 15px 22px 2px rgba(0,0,0,0.14),0px 6px 28px 5px rgba(0,0,0,0.12)",
      "0px 8px 10px -5px rgba(0,0,0,0.2),0px 16px 24px 2px rgba(0,0,0,0.14),0px 6px 30px 5px rgba(0,0,0,0.12)",
      "0px 8px 11px -5px rgba(0,0,0,0.2),0px 17px 26px 2px rgba(0,0,0,0.14),0px 6px 32px 5px rgba(0,0,0,0.12)",
      "0px 9px 11px -5px rgba(0,0,0,0.2),0px 18px 28px 2px rgba(0,0,0,0.14),0px 7px 34px 6px rgba(0,0,0,0.12)",
      "0px 9px 12px -6px rgba(0,0,0,0.2),0px 19px 29px 2px rgba(0,0,0,0.14),0px 7px 36px 6px rgba(0,0,0,0.12)",
      "0px 10px 13px -6px rgba(0,0,0,0.2),0px 20px 31px 3px rgba(0,0,0,0.14),0px 8px 38px 7px rgba(0,0,0,0.12)",
      "0px 10px 13px -6px rgba(0,0,0,0.2),0px 21px 33px 3px rgba(0,0,0,0.14),0px 8px 40px 7px rgba(0,0,0,0.12)",
      "0px 10px 14px -6px rgba(0,0,0,0.2),0px 22px 35px 3px rgba(0,0,0,0.14),0px 8px 42px 7px rgba(0,0,0,0.12)",
      "0px 11px 14px -7px rgba(0,0,0,0.2),0px 23px 36px 3px rgba(0,0,0,0.14),0px 9px 44px 8px rgba(0,0,0,0.12)",
      "none",
    ],
  })

export const theme = createTheme({
  palette,
  breakpoints: themeBreakpoints,
  typography,
  colors: {
    brand: {
      primary: "var(--primary-color)",
      variants: {
        lighter1: "var(--primary-color-lighter-1)",
        lighter2: "var(--primary-color-lighter-2)",
      },
    },
    font: {
      primary: "var(--primary-font-color)",
      variants: {
        lighter1: "var(--primary-font-color-lighter-1)",
      },
    },
    hover: {
      primary: "var(--primary-hover-color)",
    },
    border: {
      primary: "var(--primary-border-color)",
    },
    views: {
      background: "var(--primary-view-color)",
    },
  },
  zIndex: {
    z1: 10,
    z2: 20,
    z3: 30,
    z4: 40,
    z5: 50,
    z6: 60,
    z7: 70,
    z8: 80,
    z9: 90,
    z10: 100,
  },
  components: {
    ...variantMuiGrid,
    ...components,
  },
  mixins: {
    container,
    toolbar,
    footer,
    activityCircle,
    activityPath,
    checkIcon,
    wrongIcon,
    layout,
    matchingContainer,
    matchingBoxs,
    matchingBox2,
    matchingBox3,
    matchingBox4,
    matchingBox5,
    matchingBox6,
    matchingBox8,
    textMax3Lines,
    key,
    keys,
    grammarDetails,
    tabs,
    tab,
    teacherNotes,
    modalContent,
    mainLayout,
    switchButton,
  },
  shadows: [
    "none",
    "0px 3px 6px rgba(0,0,0,2%)",
    "0px 6px 6px -3px rgba(0,0,0,0.2),0px 10px 14px 1px rgba(0,0,0,0.14),0px 4px 18px 3px rgba(0,0,0,0.12)",
    "0px 3px 3px -2px rgba(0,0,0,0.2),0px 3px 4px 0px rgba(0,0,0,0.14),0px 1px 8px 0px rgba(0,0,0,0.12)",
    "0px 2px 4px -1px rgba(0,0,0,0.2),0px 4px 5px 0px rgba(0,0,0,0.14),0px 1px 10px 0px rgba(0,0,0,0.12)",
    "0 3px 10px rgb(34 34 34 / 20%)",
    "0px 0px 5px 0px rgb(153 153 153)",
    "rgb(0 0 0 / 12%) 0px 1px 6px, rgb(0 0 0 / 12%) 0px 1px 4px",
    "3px 3px 10px -1px rgba(0,0,0,0.15)",
    "0px 0px 15px -6px rgb(0 0 0 / 75%)",
    "3px 3px 10px -1px rgb(0 0 0 / 45%)",
    // defaults from 11 to 24
    "0px 6px 7px -4px rgba(0,0,0,0.2),0px 11px 15px 1px rgba(0,0,0,0.14),0px 4px 20px 3px rgba(0,0,0,0.12)",
    "0px 7px 8px -4px rgba(0,0,0,0.2),0px 12px 17px 2px rgba(0,0,0,0.14),0px 5px 22px 4px rgba(0,0,0,0.12)",
    "0px 7px 8px -4px rgba(0,0,0,0.2),0px 13px 19px 2px rgba(0,0,0,0.14),0px 5px 24px 4px rgba(0,0,0,0.12)",
    "0px 7px 9px -4px rgba(0,0,0,0.2),0px 14px 21px 2px rgba(0,0,0,0.14),0px 5px 26px 4px rgba(0,0,0,0.12)",
    "0px 8px 9px -5px rgba(0,0,0,0.2),0px 15px 22px 2px rgba(0,0,0,0.14),0px 6px 28px 5px rgba(0,0,0,0.12)",
    "0px 8px 10px -5px rgba(0,0,0,0.2),0px 16px 24px 2px rgba(0,0,0,0.14),0px 6px 30px 5px rgba(0,0,0,0.12)",
    "0px 8px 11px -5px rgba(0,0,0,0.2),0px 17px 26px 2px rgba(0,0,0,0.14),0px 6px 32px 5px rgba(0,0,0,0.12)",
    "0px 9px 11px -5px rgba(0,0,0,0.2),0px 18px 28px 2px rgba(0,0,0,0.14),0px 7px 34px 6px rgba(0,0,0,0.12)",
    "0px 9px 12px -6px rgba(0,0,0,0.2),0px 19px 29px 2px rgba(0,0,0,0.14),0px 7px 36px 6px rgba(0,0,0,0.12)",
    "0px 10px 13px -6px rgba(0,0,0,0.2),0px 20px 31px 3px rgba(0,0,0,0.14),0px 8px 38px 7px rgba(0,0,0,0.12)",
    "0px 10px 13px -6px rgba(0,0,0,0.2),0px 21px 33px 3px rgba(0,0,0,0.14),0px 8px 40px 7px rgba(0,0,0,0.12)",
    "0px 10px 14px -6px rgba(0,0,0,0.2),0px 22px 35px 3px rgba(0,0,0,0.14),0px 8px 42px 7px rgba(0,0,0,0.12)",
    "0px 11px 14px -7px rgba(0,0,0,0.2),0px 23px 36px 3px rgba(0,0,0,0.14),0px 9px 44px 8px rgba(0,0,0,0.12)",
    "none",
  ],
})
